import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChartSimple,
  faSquarePollVertical,
  faSquarePollHorizontal,
  faChartPie,
  faChartLine,
  faChartColumn,
  faChartBar,
  faPiggyBank,
  faMoneyCheckDollar,
  faMoneyBillTrendUp,
  faMoneyBillTransfer,
  faRankingStar,
  faCreditCard,
  faCoins,
  faSackDollar,
  faHandHoldingDollar,
  faUser,
  faFileSignature,
  faAddressCard,
  faToolbox,
  faPeopleGroup,
  faFileExcel,
  faTable,
  IconDefinition,
  faBriefcase,
  faUsers,
  faCloud,
  faHome,
  faTasks,
  faQuestion,
  faStore,
  faFileInvoice,
  faGear,
} from '@fortawesome/free-solid-svg-icons';
import { CustomTabItem, JWTUser, Role } from '@types';
import { MenuOption } from '@/types/menu-options';
import { Auth } from '@/context/UserContext';
import { getARDashboardAllowedClients } from '@/utils/arDashboardConfig';

export const iconList: IconDefinition[] = [
  faChartSimple,
  faSquarePollVertical,
  faSquarePollHorizontal,
  faChartPie,
  faChartLine,
  faChartColumn,
  faChartBar,
  faPiggyBank,
  faMoneyCheckDollar,
  faMoneyBillTrendUp,
  faMoneyBillTransfer,
  faRankingStar,
  faCreditCard,
  faCoins,
  faSackDollar,
  faHandHoldingDollar,
  faUser,
  faFileSignature,
  faAddressCard,
  faToolbox,
  faPeopleGroup,
  faFileExcel,
  faTable,
];

const clientOptions = [
  { icon: faHome, label: 'Home', url: '/home' },
  { icon: faUser, label: 'Account', url: '/account' },
  { icon: faCloud, label: 'Files', url: '/files' },
  { icon: faChartLine, label: 'Investor Reporting', url: '/i-reporting' },
  { icon: faQuestion, label: 'FAQ', url: '/faq' },
  { icon: faStore, label: 'Marketplace', url: '/marketplace' },
];

const investorOptions = [
  { icon: faHome, label: 'Home', url: '/i-home' },
  { icon: faCloud, label: 'All Files', url: '/i-files' },
  { icon: faUser, label: 'Account', url: '/i-account' },
  { icon: faChartLine, label: 'Report Builder', url: '/i-reporting' },
];

export const getIconDefinitionByName = (iconName: string) =>
  iconList.find(
    (icon) => icon.iconName.toLowerCase() === iconName.toLowerCase()
  );

export const MENU_BY_USER = {
  superadmin: [
    { icon: faBriefcase, label: 'Clients', url: '/clients' },
    { icon: faUsers, label: 'Users', url: '/users' },
    { icon: faCloud, label: 'OneDrive', url: '/files' },
    { icon: faGear, label: 'Settings', url: '/settings' },
  ],
  specialadmin: [
    { icon: faBriefcase, label: 'Clients', url: '/clients' },
    { icon: faCloud, label: 'OneDrive', url: '/files' },
  ],
  admin: [
    { icon: faBriefcase, label: 'Clients', url: '/clients' },
    { icon: faCloud, label: 'OneDrive', url: '/files' },
  ],
  client: clientOptions,
  clientInvestor: [
    { icon: faHome, label: 'Home', url: '/home' },
    { icon: faCloud, label: 'Files', url: '/files' },
  ],
  investor: investorOptions,
};

export const BOTTOM_MENU_BY_USER = {
  superadmin: [] as MenuOption[],
  specialadmin: [] as MenuOption[],
  admin: [] as MenuOption[],
  client: [] as MenuOption[],
  investor: [] as MenuOption[],
};

export const MENU_ADMIN_EXCLUSIVES = [
  {
    icon: faTasks,
    label: 'Categorizations',
    url: '/categorizations',
  },
];

/**
 * Entries only a staff user viewing a client sees. Unlike MENU_ADMIN_EXCLUSIVES, which the
 * Categorizations entry also gives to a client user, the Tax Profile page is behind
 * validAdminsRoles in Router.tsx, so a client user and an investor must not be offered it.
 */
export const MENU_STAFF_VIEWING_CLIENT = [
  {
    icon: faFileInvoice,
    label: 'Tax Profile',
    url: '/tax-profile',
  },
];

export const getMenuOptions = (
  menuType: Role,
  auth: Auth | null | undefined,
  customTabs: MenuOption[],
  impersonatingAccountId: number | null | undefined,
  showTransactionsTab: boolean
) => {
  const arDashboardAllowedClients: number[] = getARDashboardAllowedClients();
  let options = [
    ...MENU_BY_USER[menuType],
    ...(auth?.isInvestor ? [] : customTabs),
  ];

  // Conditionally add the Transactions tab
  if (
    ((auth?.isAdmin && impersonatingAccountId) || auth?.isClient) &&
    !!showTransactionsTab
  ) {
    options = [...options, ...MENU_ADMIN_EXCLUSIVES];
  }

  // The Tax Profile page is for the tax team, so only a staff user viewing a client is offered it.
  if (auth?.isAdmin && impersonatingAccountId) {
    options = [...options, ...MENU_STAFF_VIEWING_CLIENT];
  }

  // Remove the /account option for investors when impersonating
  if (impersonatingAccountId && auth?.role === 'investor') {
    options = options.filter((option) => option.url !== '/account');
  }

  if (auth?.role === 'investor' && auth?.firms?.length === 0) {
    options = options.filter((option) => option.url !== '/i-account');
  }

  if (auth?.role === 'investor') {
    options = options.filter((option) => option.url !== '/ar-dashboard');
  }

  if (
    arDashboardAllowedClients.length > 0 &&
    auth?.isAdmin &&
    impersonatingAccountId &&
    arDashboardAllowedClients.includes(impersonatingAccountId)
    //   ||
    // (auth?.isClient &&
    //   auth?.clients?.[0]?.id &&
    //   arDashboardAllowedClients.includes(auth?.clients?.[0]?.id)) SC-3397: first iteration only allowed admin roles for AR snapshot access
  ) {
    options = [
      ...options,
      ...[{ icon: faTable, label: 'AR Dashboard', url: '/ar-dashboard' }],
    ];
  }

  return options as MenuOption[];
};

export const getBottomMenuOptions = (
  menuType: Role,
  auth: Auth | null | undefined,
  impersonatingAccountId: number | null | undefined
) => {
  let options = [...BOTTOM_MENU_BY_USER[menuType]];

  // Remove the /account option for investors when impersonating
  if (impersonatingAccountId && auth?.role === 'investor') {
    options = options.filter((option) => option.url !== '/account');
  }

  if (auth?.role === 'investor' && auth?.firms?.length === 0) {
    options = options.filter((option) => option.url !== '/i-account');
  }

  return options;
};

export const prepareCustomTabs = (
  customTabInfo: CustomTabItem[] | null
): MenuOption[] => {
  return (customTabInfo || []).map((customTab: CustomTabItem) => ({
    icon: getIconDefinitionByName(customTab.icon) as IconProp,
    label: customTab.titleOfTab,
    url: `/custom-tab-option/${encodeURIComponent(
      customTab.titleOfTab
    )}?embeddedLink=${encodeURIComponent(
      customTab.embeddedLink
    )}&documentLink=${encodeURIComponent(customTab.documentLink)}`,
  }));
};

export const menuType = (
  impersonatingAccountId: number | null,
  auth: Auth | null | undefined
) => {
  if (impersonatingAccountId && auth?.isAdmin) {
    return 'client';
  }
  return (auth as JWTUser)?.role || 'client';
};
